(() => {
  'use strict';

  const currencyScale = (currency) => {
    try {
      return 10 ** new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    } catch (_error) {
      return 100;
    }
  };

  const formatMoney = (minorUnits, currency = 'USD') => {
    const amount = Number(minorUnits) || 0;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / currencyScale(currency));
    } catch (_error) {
      if (globalThis.Shopify && typeof globalThis.Shopify.formatMoney === 'function') {
        return globalThis.Shopify.formatMoney(amount);
      }
      return String(amount);
    }
  };

  globalThis.CozyFactoryTest = { formatMoney };

  const parseVariants = (root) => {
    const node = root.querySelector('[data-cozy-variants]');
    if (!node) return [];
    try {
      const variants = JSON.parse(node.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (_error) {
      return [];
    }
  };

  const updateCartCount = async () => {
    const cartRoute = (window.routes && window.routes.cart_url) || '/cart';
    try {
      const response = await fetch(`${cartRoute}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cozy-cart-count]').forEach((count) => {
        count.textContent = String(cart.item_count || 0);
      });
    } catch (_error) {
      // The add succeeded; a count refresh failure must not turn it into an error.
    }
  };

  const announce = (root, message, isError = false) => {
    const live = root.querySelector('[data-cozy-live]');
    if (!live) return;
    live.textContent = message;
    live.classList.toggle('is-error', isError);
  };

  const openConfiguredCart = (item) => {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { item, source: 'cozy-product-form' } }));
    const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };

  const initProduct = (root) => {
    if (!root || root.hasAttribute('data-cozy-initialized')) return;
    root.setAttribute('data-cozy-initialized', 'true');
    const form = root.querySelector('[data-cozy-form]');
    const select = root.querySelector('[data-cozy-variant]');
    const submit = root.querySelector('[data-cozy-submit]');
    const image = root.querySelector('[data-cozy-image]');

    // buy-box gallery: the thumbnail strip switches which photo is on the stage
    const gallery = root.querySelector('[data-cozy-gallery]');
    if (gallery) {
      const shots = Array.from(gallery.querySelectorAll('[data-cozy-gallery-image]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-cozy-gallery-thumb]'));
      const reveal = (index) => {
        shots.forEach((shot, i) => {
          if (i === index) {
            shot.removeAttribute('hidden');
          } else {
            shot.setAttribute('hidden', '');
          }
        });
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
      };
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => reveal(i)));
      if (shots.length > 1) reveal(0);
    }
    const price = root.querySelector('[data-cozy-price]');
    const variants = parseVariants(root);
    if (!form || !select || !submit || !variants.length) return;
    const currency = root.dataset.currency || 'USD';

    const variantById = () => variants.find((variant) => String(variant.id) === String(select.value));
    const synchronize = () => {
      const variant = variantById();
      if (!variant) return;
      root.querySelectorAll('[data-cozy-pill]').forEach((pill) => {
        pill.classList.toggle('is-active', String(pill.dataset.value) === String(select.value));
      });
      if (price) price.textContent = formatMoney(variant.price, currency);
      if (image && variant.featured_image) {
        image.src = variant.featured_image.src;
        image.alt = variant.featured_image.alt || variant.title;
        if (variant.featured_image.width) image.width = variant.featured_image.width;
        if (variant.featured_image.height) image.height = variant.featured_image.height;
      }
      submit.disabled = !variant.available;
      submit.textContent = variant.available ? `Add to cart — ${formatMoney(variant.price, currency)}` : 'Unavailable';
    };

    form.addEventListener('change', synchronize);
    root.querySelectorAll('[data-cozy-pill]').forEach((pill) => {
      pill.addEventListener('click', () => {
        if (pill.disabled) return;
        select.value = pill.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    form.addEventListener('submit', async (event) => {
      if (submit.disabled || submit.dataset.pending === 'true') return;
      event.preventDefault();
      submit.dataset.pending = 'true';
      submit.disabled = true;
      announce(root, 'Adding your design to cart.');
      const addRoute = window.routes && window.routes.cart_add_url;
      if (!addRoute) {
        form.submit();
        return;
      }
      try {
        const response = await fetch(addRoute, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'This design could not be added.');
        await updateCartCount();
        announce(root, 'Added to cart.');
        openConfiguredCart(payload);
      } catch (error) {
        announce(root, error instanceof Error ? error.message : 'This design could not be added.', true);
      } finally {
        submit.dataset.pending = 'false';
        synchronize();
      }
    });
    synchronize();
  };


  const initBundle = (root) => {
    if (!root || root.hasAttribute('data-cozy-bundle-init')) return;
    root.setAttribute('data-cozy-bundle-init', 'true');
    const form = root.querySelector('[data-cozy-bundle-form]');
    const sizes = root.querySelectorAll('[data-cozy-bundle-size]');
    const variantInput = root.querySelector('[data-cozy-bundle-variant]');
    const quantityInput = root.querySelector('[data-cozy-bundle-qty-input]');
    const picks = root.querySelectorAll('[data-cozy-bundle-pick]');
    const submit = root.querySelector('[data-cozy-bundle-submit]');
    const live = root.querySelector('[data-cozy-live]');
    if (!form || !variantInput || !sizes.length) return;

    const setSize = (size) => {
      sizes.forEach((b) => {
        const active = String(b.dataset.cozyBundleSize) === String(size);
        b.classList.toggle('is-active', active);
        // a bundle is now a quantity of the supplier-linked variant, so the supplier can
        // fulfil it; the tier price comes from a native automatic discount
        if (active && quantityInput && b.dataset.cozyBundleQty) quantityInput.value = b.dataset.cozyBundleQty;
      });
      picks.forEach((p) => {
        const n = parseInt(p.dataset.cozyBundlePick, 10);
        const visible = n <= size;
        p.style.display = visible ? '' : 'none';
        const sel = p.querySelector('select');
        if (sel) sel.disabled = !visible;
      });
    };
    sizes.forEach((b) => b.addEventListener('click', () => setSize(parseInt(b.dataset.cozyBundleSize, 10))));
    setSize(3);

    form.addEventListener('submit', async (event) => {
      if (submit.disabled || submit.dataset.pending === 'true') return;
      event.preventDefault();
      submit.dataset.pending = 'true';
      submit.disabled = true;
      if (live) live.textContent = 'Adding your bundle to cart.';
      const addRoute = window.routes && window.routes.cart_add_url;
      if (!addRoute) { form.submit(); return; }
      try {
        const response = await fetch(addRoute, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'The bundle could not be added.');
        if (live) live.textContent = 'Added to cart.';
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { item: payload, source: 'cozy-bundle-form' } }));
        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      } catch (error) {
        if (live) live.textContent = error instanceof Error ? error.message : 'The bundle could not be added.';
      } finally {
        submit.dataset.pending = 'false';
        submit.disabled = false;
      }
    });
  };

  const init = (scope = document) => {
    if (scope.matches && scope.matches('[data-cozy-product]')) initProduct(scope);
    if (scope.matches && scope.matches('[data-cozy-bundle]')) initBundle(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll('[data-cozy-product]').forEach(initProduct);
      scope.querySelectorAll('[data-cozy-bundle]').forEach(initBundle);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  if (!globalThis.__cozyFactoryListeners) {
    globalThis.__cozyFactoryListeners = true;
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('click', (event) => {
      const chooser = event.target.closest ? event.target.closest('[data-cozy-choose-variant]') : null;
      if (!chooser) return;
      const variantId = chooser.dataset.cozyChooseVariant;
      const root = document.querySelector('[data-cozy-product]');
      const select = root && root.querySelector('[data-cozy-variant]');
      if (!select || !Array.from(select.options).some((option) => option.value === variantId && !option.disabled)) return;
      select.value = variantId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      root.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      select.focus({ preventScroll: true });
      const confirmation = document.querySelector('[data-cozy-variant-confirmation]');
      if (confirmation) confirmation.textContent = `${select.options[select.selectedIndex].text.split(' — ')[0]} selected.`;
    });
  }
})();


// Urgency: persistent evergreen countdown + one-time discount popup.
(() => {
  'use strict';
  const DURATION_MS = 3 * 60 * 60 * 1000;
  const END_KEY = 'cozy_sale_end';
  const POP_KEY = 'cozy_popup_shown';

  const read = (key) => { try { return localStorage.getItem(key); } catch (_e) { return null; } };
  const write = (key, value) => { try { localStorage.setItem(key, value); } catch (_e) { /* ignore */ } };

  // Persistent per-visitor deadline; resets to a fresh cycle when it expires.
  let deadline = parseInt(read(END_KEY), 10) || 0;
  if (!deadline || deadline <= Date.now()) {
    deadline = Date.now() + DURATION_MS;
    write(END_KEY, String(deadline));
  }

  const fmt = (ms) => {
    if (ms < 0) ms = 0;
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => (n < 10 ? '0' : '') + n;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  const nodes = document.querySelectorAll('[data-cozy-countdown]');
  if (nodes.length) {
    const tick = () => {
      let remaining = deadline - Date.now();
      if (remaining <= 0) {
        deadline = Date.now() + DURATION_MS;
        write(END_KEY, String(deadline));
        remaining = DURATION_MS;
      }
      const text = fmt(remaining);
      nodes.forEach((node) => { node.textContent = text; });
    };
    tick();
    setInterval(tick, 1000);
  }

  // Discount popup: shown once per visitor.
  const popup = document.querySelector('[data-cozy-popup]');
  if (popup && read(POP_KEY) !== '1') {
    const close = () => {
      popup.classList.remove('is-open');
      setTimeout(() => { popup.hidden = true; }, 250);
    };
    popup.querySelectorAll('[data-cozy-popup-close]').forEach((el) => {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !popup.hidden) close();
    });
    setTimeout(() => {
      popup.hidden = false;
      requestAnimationFrame(() => popup.classList.add('is-open'));
      write(POP_KEY, '1');
    }, 1200);
  }
})();


// Sticky add-to-cart bar (appears when the main add button scrolls out of view)
(() => {
  'use strict';
  const sticky = document.querySelector('[data-cozy-sticky]');
  if (!sticky) return;
  const addBtn = sticky.querySelector('[data-cozy-sticky-add]');
  const stickyImg = sticky.querySelector('[data-cozy-sticky-img]');
  const stickyPrice = sticky.querySelector('[data-cozy-sticky-price]');
  const stickySave = sticky.querySelector('[data-cozy-sticky-save]');
  const mainSection = document.querySelector('[data-cozy-product]');
  const mainSubmit = mainSection && mainSection.querySelector('[data-cozy-submit]');
  const mainSelect = mainSection && mainSection.querySelector('[data-cozy-variant]');
  const mainPrice = mainSection && mainSection.querySelector('[data-cozy-price]');
  const mainSave = mainSection && mainSection.querySelector('.cozy-save');
  const mainImage = mainSection && mainSection.querySelector('[data-cozy-image]');

  const sync = () => {
    if (mainPrice && stickyPrice) stickyPrice.textContent = mainPrice.textContent;
    if (mainSave && stickySave) stickySave.textContent = mainSave.textContent;
    if (mainImage && stickyImg) { stickyImg.src = mainImage.src; stickyImg.alt = mainImage.alt; }
  };
  sync();

  const onScroll = () => {
    if (!mainSubmit) { sticky.hidden = true; return; }
    const r = mainSubmit.getBoundingClientRect();
    const outOfView = r.bottom < 0;
    if (outOfView) {
      sticky.hidden = false;
      requestAnimationFrame(() => sticky.classList.add('is-visible'));
    } else {
      sticky.classList.remove('is-visible');
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper) pageWrapper.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const form = mainSection && mainSection.querySelector('[data-cozy-form]');
  if (form) form.addEventListener('change', sync);

  addBtn.addEventListener('click', async () => {
    if (!mainSelect || addBtn.dataset.pending === 'true') return;
    addBtn.dataset.pending = 'true';
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';
    const addRoute = window.routes && window.routes.cart_add_url;
    if (!addRoute) { addBtn.textContent = 'Add to cart'; addBtn.disabled = false; delete addBtn.dataset.pending; return; }
    try {
      const body = new FormData();
      body.append('id', mainSelect.value);
      body.append('quantity', '1');
      const response = await fetch(addRoute, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add to cart.');
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { item: payload, source: 'cozy-sticky' } }));
      const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      addBtn.textContent = 'Added ✓';
      setTimeout(() => { addBtn.textContent = 'Add to cart'; }, 1500);
    } catch (_error) {
      addBtn.textContent = 'Add to cart';
    } finally {
      addBtn.dataset.pending = 'false';
      addBtn.disabled = false;
    }
  });
})();
